/**
 * The client's name, in whichever shape the API reports it.
 *
 * `/folloportal/me` used to serve `client_firstname` / `client_lastname`. Those
 * were derived server-side by splitting the person's name on its first space,
 * which is wrong for every name that is not one person's — "Ryan & Lauren
 * Dankoff" came back with "& Lauren Dankoff" as a surname. They are gone from
 * the API. What arrives now is the shape the client's contact actually holds:
 * the two halves when somebody typed them into a two-field form, or `fullname`
 * for a name the clients-to-contacts backfill could not correctly split.
 */
export interface ClientNameAttributes {
  first_name?: string | null;
  last_name?: string | null;
  fullname?: string | null;
  client_contact?: string | null;
}

const clean = (value?: string | null): string => value?.trim() ?? "";

/**
 * Whether this client edits ONE name field. A contact keeps a `fullname` it
 * already had and is never given one, so this is a property of the client's
 * existing data — it does not flip because of what the customer types.
 */
export const editsFullname = (attributes?: ClientNameAttributes | null): boolean =>
  clean(attributes?.fullname) !== "";

/** The whole name, from whichever shape holds it. */
export const clientDisplayName = (attributes?: ClientNameAttributes | null): string => {
  const fullname = clean(attributes?.fullname);

  if (fullname !== "") {
    return fullname;
  }

  const halves = [attributes?.first_name, attributes?.last_name].map(clean).filter(Boolean).join(" ");

  return halves !== "" ? halves : clean(attributes?.client_contact);
};

/**
 * A cardholder given/family name for the payment gateway, which has nowhere to
 * put a one-field name.
 *
 * This is the same lossy first-space split the API stopped doing — done here on
 * purpose, because nothing is STORED from it: it only prefills the card form,
 * where a wrong surname costs the customer one correction instead of becoming
 * their record.
 */
export const cardholderName = (
  attributes?: ClientNameAttributes | null,
): { firstName: string; lastName: string } => {
  const firstName = clean(attributes?.first_name);
  const lastName = clean(attributes?.last_name);

  if (firstName !== "" || lastName !== "") {
    return { firstName, lastName };
  }

  const [head, ...rest] = clientDisplayName(attributes).split(/\s+/).filter(Boolean);

  return { firstName: head ?? "", lastName: rest.join(" ") };
};
